import {
  Camera,
  CameraOff,
  ExternalLink,
  Headphones,
  HeadphoneOff,
  Mic,
  MicOff,
  MonitorUp,
  Phone,
  PhoneIncoming,
  PhoneMissed,
  PhoneOff,
  Popcorn,
  Square,
  X,
} from "lucide-react";
import { useEffect, useState, type ReactElement } from "react";
import { Track, type Participant } from "livekit-client";

import { Avatar, AvatarFallback, AvatarImage, Button, Input, cn } from "@link/ui";

import {
  callPeer,
  participantIsScreenSharing,
  participantVideoTrack,
  useMediaSession,
  type WatchService,
} from "../../state/MediaSession";
import "./call-surface.css";
import { useSelf } from "../../state/SelfSession";
import { CallError } from "./CallError";
import { CallVideoTile } from "./CallVideoTile";
import { FloatingCallPanel } from "./FloatingCallPanel";
import { returnToCall } from "../../lib/call-navigation";

const SERVICES: Array<{ id: WatchService; label: string }> = [
  { id: "youtube", label: "YouTube" },
  { id: "netflix", label: "Netflix" },
  { id: "disney", label: "Disney+" },
  { id: "prime", label: "Prime Video" },
  { id: "hulu", label: "Hulu" },
  { id: "other", label: "Other" },
];

export function MediaDock({ inlineDmXid = null }: { inlineDmXid?: string | null }) {
  const media = useMediaSession();
  const [minimizedCallXid, setMinimizedCallXid] = useState<string | null>(null);
  const [watchOpen, setWatchOpen] = useState(false);
  const [service, setService] = useState<WatchService>("youtube");
  const [url, setUrl] = useState("");
  const [watchError, setWatchError] = useState<string | null>(null);
  useRingtone(media.incomingCall !== null, "incoming");
  useRingtone(media.outgoingCall !== null, "outgoing");

  const alerts = <CallAlerts suppressDmXid={inlineDmXid} />;
  const activeCallIsInline = inlineDmXid !== null
    && media.activeCall?.dm_xid === inlineDmXid;
  const activeCallXid = media.activeCall?.call_xid ?? (media.target ? `${media.target.context}:${media.target.xid}` : null);
  const openCall = () => returnToCall(media.target, media.activeCall?.dm_xid);
  const callIsMinimized = activeCallXid !== null
    && minimizedCallXid === activeCallXid;

  useEffect(() => {
    if (activeCallXid === null || activeCallIsInline) {
      setMinimizedCallXid(null);
    }
  }, [activeCallIsInline, activeCallXid]);

  // The active DM already renders a full call stage. Keep remote audio
  // mounted, but remove the duplicate floating controls from that view.
  if (activeCallIsInline) {
    return (
      <>
        {alerts}
        <RemoteAudio participants={media.participants} />
      </>
    );
  }

  if (!media.connected && !media.connecting && media.error === null) return alerts;

  if (!media.connected) {
    return (
      <>
        {alerts}
        <div className="fixed bottom-[calc(4.75rem+env(safe-area-inset-bottom))] left-1/2 z-50 w-[min(420px,calc(100vw-1.5rem))] -translate-x-1/2 rounded-2xl border border-cloud-200 bg-white px-4 py-3 shadow-2xl lg:bottom-5 lg:w-auto">
          <p className="text-sm font-semibold text-cloud-800">
            {media.connecting ? "Joining call…" : "Couldn’t join the call"}
          </p>
          {media.error !== null ? <CallError message={media.error} /> : null}
        </div>
      </>
    );
  }

  const isHost =
    media.watchParty?.hostIdentity === media.room?.localParticipant.identity;

  if (callIsMinimized) {
    return (
      <>
        {alerts}
        <button
          type="button"
          onClick={() => {
            openCall();
            setMinimizedCallXid(null);
          }}
          className="fixed bottom-[calc(4.75rem+env(safe-area-inset-bottom))] right-3 z-50 flex max-w-[calc(100vw-1.5rem)] items-center gap-3 rounded-2xl border border-emerald-300 bg-white/95 px-3 py-2.5 text-left shadow-2xl backdrop-blur-xl transition hover:-translate-y-0.5 hover:border-emerald-400 dark:border-emerald-700 lg:bottom-4 lg:right-4"
          aria-label="Return to call"
        >
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300">
            <Phone className="h-4 w-4" />
          </span>
          <span className="min-w-0">
            <span className="block truncate text-sm font-semibold text-cloud-800">
              {media.target?.label ?? "Link call"}
            </span>
            <span className="block text-[11px] text-cloud-500">
              Call minimized · {media.participants.length} connected
            </span>
          </span>
        </button>
        <RemoteAudio participants={media.participants} />
      </>
    );
  }

  return (
    <>
    {alerts}
    <FloatingCallPanel label={media.target?.label ?? "Link call"} count={media.participants.length}
      onReturn={openCall} onMinimize={() => setMinimizedCallXid(activeCallXid)}>

      {media.participants.some((participant) => participantVideoTrack(participant) !== null) ? (
        <div className="grid max-h-[30vh] grid-cols-2 gap-1 overflow-auto bg-cloud-950 p-1">
          {media.participants.map((participant) => (
            <ParticipantTile key={participant.identity} participant={participant} />
          ))}
        </div>
      ) : null}

      {media.watchParty !== null ? (
        <div className="flex flex-wrap items-center gap-3 border-b border-cloud-200 bg-grape-50 px-4 py-3">
          <Popcorn className="h-4 w-4 text-grape-600" />
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-cloud-800">Watch party invitation</p>
            <p className="truncate text-xs text-cloud-500">{media.watchParty.url}</p>
          </div>
          {!media.watchPartyAccepted ? (
            <Button size="sm" onClick={media.acceptWatchParty}>Accept</Button>
          ) : (
            <Button size="sm" asChild>
              <a href={media.watchParty.url} target="_blank" rel="noreferrer">
                Open service <ExternalLink className="ml-1.5 h-3.5 w-3.5" />
              </a>
            </Button>
          )}
          {isHost ? (
            <Button size="sm" variant="outline" onClick={() => void media.endWatchParty()}>
              End
            </Button>
          ) : null}
        </div>
      ) : null}

      {watchOpen && media.watchParty === null ? (
        <div className="border-b border-cloud-200 px-4 py-3">
          <div className="flex flex-wrap gap-2">
            {SERVICES.map((item) => (
              <button
                type="button"
                key={item.id}
                onClick={() => setService(item.id)}
                className={cn(
                  "rounded-full px-3 py-1 text-xs font-semibold ring-1 transition-colors",
                  service === item.id
                    ? "bg-grape-600 text-white ring-grape-600"
                    : "bg-white text-cloud-600 ring-cloud-200 hover:ring-grape-300",
                )}
              >
                {item.label}
              </button>
            ))}
          </div>
          <div className="mt-2 flex gap-2">
            <Input
              value={url}
              onChange={(event) => setUrl(event.target.value)}
              placeholder="Paste the exact show or video link"
              className="h-9"
            />
            <Button
              size="sm"
              onClick={() => {
                setWatchError(null);
                void media
                  .startWatchParty(service, url)
                  .then(() => setWatchOpen(false))
                  .catch((cause: unknown) =>
                    setWatchError(cause instanceof Error ? cause.message : "Invalid link."),
                  );
              }}
            >
              Invite
            </Button>
          </div>
          <p className="mt-1.5 text-[11px] text-cloud-500">
            Everyone chooses whether to join and watches through their own account. Use screen share for services that allow it.
          </p>
          {watchError !== null ? <p className="mt-1 text-xs text-rose-600">{watchError}</p> : null}
        </div>
      ) : null}

      <div className="call-control-dock" role="group" aria-label="Call controls">
        <ControlButton
          active={media.microphoneEnabled}
          onClick={() => void media.toggleMicrophone()}
          label={media.microphoneEnabled ? "Mute" : "Unmute"}
          icon={media.microphoneEnabled ? <Mic /> : <MicOff />}
        />
        <ControlButton
          active={media.cameraEnabled}
          onClick={() => void media.toggleCamera()}
          label={media.cameraEnabled ? "Camera off" : "Camera on"}
          icon={media.cameraEnabled ? <Camera /> : <CameraOff />}
        />
        <ControlButton
          active={!media.deafened}
          onClick={media.toggleDeafen}
          label={media.deafened ? "Undeafen" : "Deafen"}
          icon={media.deafened ? <HeadphoneOff /> : <Headphones />}
        />
        <ControlButton
          active={media.screenShareEnabled}
          onClick={() => void media.toggleScreenShare()}
          label={media.screenShareEnabled ? "Stop sharing" : "Share screen"}
          icon={media.screenShareEnabled ? <Square /> : <MonitorUp />}
        />
        <ControlButton
          active={watchOpen || media.watchParty !== null}
          onClick={() => setWatchOpen((value) => !value)}
          label="Watch party"
          icon={<Popcorn />}
        />
        <button
          type="button"
          onClick={() => void media.leave()}
          className="ml-2 flex h-10 w-12 items-center justify-center rounded-xl bg-rose-600 text-white hover:bg-rose-700"
          aria-label="Leave call"
        >
          <PhoneOff className="h-4 w-4" />
        </button>
      </div>
      <RemoteAudio participants={media.participants} />
    </FloatingCallPanel>
    </>
  );
}

function CallAlerts({ suppressDmXid }: { suppressDmXid: string | null }) {
  const media = useMediaSession();
  const self = useSelf();
  const incomingPeer = media.incomingCall === null ? null : callPeer(media.incomingCall, self.identity.accountXid);
  const outgoingPeer = media.outgoingCall === null ? null : callPeer(media.outgoingCall, self.identity.accountXid);
  const missed = media.missedCalls[0] ?? null;
  const missedPeer = missed === null ? null : callPeer(missed, self.identity.accountXid);

  return (
    <div className="pointer-events-none fixed right-3 top-[max(0.75rem,env(safe-area-inset-top))] z-[90] flex w-[min(370px,calc(100vw-1.5rem))] flex-col gap-3 sm:right-4 sm:top-4 sm:w-[min(370px,calc(100vw-2rem))]">
      {media.incomingCall !== null
      && incomingPeer !== null
      && media.incomingCall.dm_xid !== suppressDmXid ? (
        <article className="pointer-events-auto overflow-hidden rounded-3xl border border-strawberry-300 bg-[hsl(var(--card))] shadow-[0_24px_70px_rgba(20,24,40,0.28)]">
          <div className="h-2 bg-gradient-to-r from-strawberry-400 via-strawberry-500 to-grape-500" />
          <div className="flex items-center gap-4 p-5">
            <CallAvatar name={incomingPeer.display_name} src={incomingPeer.avatar_url} ringing />
            <div className="min-w-0 flex-1">
              <p className="truncate text-base font-bold text-[hsl(var(--card-foreground))]">{incomingPeer.display_name}</p>
              <p className="mt-0.5 flex items-center gap-1.5 text-sm text-[hsl(var(--muted-foreground))]">
                <PhoneIncoming className="h-3.5 w-3.5" />
                Incoming {media.incomingCall.with_video ? "video" : "voice"} call
              </p>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3 px-5 pb-5">
            <button
              type="button"
              className="flex h-11 items-center justify-center gap-2 rounded-xl bg-rose-600 text-sm font-bold text-white transition hover:bg-rose-700 disabled:cursor-wait disabled:opacity-55"
              onClick={() => void media.declineCall(media.incomingCall!)}
              disabled={media.connecting}
            >
              <PhoneOff className="h-4 w-4" /> Decline
            </button>
            <button
              type="button"
              className="flex h-11 items-center justify-center gap-2 rounded-xl bg-emerald-500 text-sm font-bold text-white transition hover:bg-emerald-600 disabled:cursor-wait disabled:opacity-55"
              onClick={() => void media.acceptCall(media.incomingCall!)}
              disabled={media.connecting}
            >
              <Phone className="h-4 w-4" /> Accept
            </button>
          </div>
        </article>
      ) : null}

      {media.outgoingCall !== null
      && outgoingPeer !== null
      && media.outgoingCall.dm_xid !== suppressDmXid ? (
        <article className="pointer-events-auto rounded-3xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-5 shadow-2xl">
          <div className="flex items-center gap-4">
            <CallAvatar name={outgoingPeer.display_name} src={outgoingPeer.avatar_url} ringing />
            <div className="min-w-0 flex-1">
              <p className="truncate text-base font-bold text-[hsl(var(--card-foreground))]">{outgoingPeer.display_name}</p>
              <p className="mt-0.5 text-sm text-[hsl(var(--muted-foreground))]">Ringing…</p>
            </div>
            <button
              type="button"
              className="flex h-11 w-11 items-center justify-center rounded-full bg-rose-600 text-white hover:bg-rose-700"
              onClick={() => void media.cancelCall()}
              aria-label="Cancel call"
            >
              <PhoneOff className="h-5 w-5" />
            </button>
          </div>
        </article>
      ) : null}

      {media.activeCall !== null
      && media.activeCall.dm_xid !== suppressDmXid
      && !media.connected
      && !media.connecting ? (
        <article className="pointer-events-auto flex items-center gap-3 rounded-2xl border border-emerald-300 bg-[hsl(var(--card))] p-4 shadow-xl">
          <span className="flex h-10 w-10 items-center justify-center rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300"><Phone className="h-4 w-4" /></span>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-bold text-[hsl(var(--card-foreground))]">Call in progress</p>
            <p className="truncate text-xs text-[hsl(var(--muted-foreground))]">You can return after reconnecting.</p>
          </div>
          <Button size="sm" onClick={() => void media.rejoinCall()}>Join</Button>
        </article>
      ) : null}

      {missed !== null && missedPeer !== null ? (
        <article className="pointer-events-auto flex items-center gap-3 rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-4 shadow-xl">
          <span className="flex h-10 w-10 items-center justify-center rounded-full bg-rose-100 text-rose-600 dark:bg-rose-950 dark:text-rose-300"><PhoneMissed className="h-4 w-4" /></span>
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-bold text-[hsl(var(--card-foreground))]">Missed call from {missedPeer.display_name}</p>
            <p className="text-xs text-[hsl(var(--muted-foreground))]">{new Date(missed.started_at).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}</p>
          </div>
          <button type="button" className="rounded-lg p-2 text-cloud-400 hover:bg-cloud-100 hover:text-cloud-700 dark:hover:text-white" onClick={() => media.dismissMissedCall(missed.call_xid)} aria-label="Dismiss missed call"><X className="h-4 w-4" /></button>
        </article>
      ) : null}
      {media.callNotice !== null ? (
        <article className="pointer-events-auto flex items-center gap-3 rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-4 shadow-xl">
          <span className="flex h-10 w-10 items-center justify-center rounded-full bg-cloud-100 text-cloud-600"><PhoneOff className="h-4 w-4" /></span>
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-bold text-[hsl(var(--card-foreground))]">{media.callNotice.title}</p>
            <p className="truncate text-xs text-[hsl(var(--muted-foreground))]">{media.callNotice.body}</p>
          </div>
          <button type="button" className="rounded-lg p-2 text-cloud-400 hover:bg-cloud-100 hover:text-cloud-700 dark:hover:text-white" onClick={media.dismissCallNotice} aria-label="Dismiss call notification"><X className="h-4 w-4" /></button>
        </article>
      ) : null}
    </div>
  );
}

function CallAvatar({ name, src, ringing = false }: { name: string; src: string | null; ringing?: boolean }) {
  return (
    <div className="relative shrink-0">
      {ringing ? <span className="absolute inset-0 animate-ping rounded-full bg-strawberry-300/50" /> : null}
      <Avatar className="relative h-14 w-14 ring-4 ring-white">
        {src !== null ? <AvatarImage src={src} alt="" /> : null}
        <AvatarFallback>{name.slice(0, 2).toUpperCase()}</AvatarFallback>
      </Avatar>
    </div>
  );
}

function useRingtone(enabled: boolean, kind: "incoming" | "outgoing") {
  useEffect(() => {
    if (!enabled || typeof window === "undefined") return;
    let context: AudioContext | null = null;
    const play = (): void => {
      try {
        context ??= new AudioContext();
        const now = context.currentTime;
        const notes = kind === "incoming" ? [440, 554] : [392];
        notes.forEach((frequency, index) => {
          const oscillator = context!.createOscillator();
          const gain = context!.createGain();
          oscillator.frequency.value = frequency;
          oscillator.type = "sine";
          gain.gain.setValueAtTime(0.0001, now + index * 0.24);
          gain.gain.exponentialRampToValueAtTime(0.12, now + index * 0.24 + 0.025);
          gain.gain.exponentialRampToValueAtTime(0.0001, now + index * 0.24 + 0.2);
          oscillator.connect(gain).connect(context!.destination);
          oscillator.start(now + index * 0.24);
          oscillator.stop(now + index * 0.24 + 0.22);
        });
      } catch {
        // Browser autoplay policy can block sound until the first interaction.
      }
    };
    play();
    const timer = window.setInterval(play, kind === "incoming" ? 1900 : 2600);
    return () => {
      window.clearInterval(timer);
      void context?.close();
    };
  }, [enabled, kind]);
}

function ControlButton({
  active,
  onClick,
  label,
  icon,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
  icon: ReactElement;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={label}
      aria-label={label}
      data-active={active ? "true" : "false"}
      className={cn(
        "flex h-10 w-10 items-center justify-center rounded-xl transition-colors [&_svg]:h-4 [&_svg]:w-4",
        active
          ? "bg-strawberry-100 text-strawberry-700"
          : "bg-cloud-100 text-cloud-500 hover:bg-cloud-200",
      )}
    >
      {icon}
    </button>
  );
}

function ParticipantTile({ participant }: { participant: Participant }) {
  return <CallVideoTile track={participantVideoTrack(participant)}
    label={participant.name || participant.identity} screenShare={participantIsScreenSharing(participant)}
    speaking={participant.isSpeaking && participant.isMicrophoneEnabled} muted={!participant.isMicrophoneEnabled} />;
}

function RemoteAudio({ participants }: { participants: Participant[] }) {
  const self = useSelf();
  return (
    <>
      {participants.flatMap((participant) =>
        [...participant.trackPublications.values()]
          .filter(
            (publication) =>
              !participant.isLocal &&
              publication.source === Track.Source.Microphone &&
              publication.track !== undefined,
          )
          .map((publication) => (
            <AudioTrack key={publication.trackSid} track={publication.track!} muted={self.deafened} />
          )),
      )}
    </>
  );
}

function AudioTrack({ track, muted }: { track: Track; muted: boolean }) {
  const [element, setElement] = useState<HTMLAudioElement | null>(null);
  useEffect(() => {
    if (track === undefined || element === null) return;
    track.attach(element);
    return () => {
      track.detach(element);
    };
  }, [track, element]);
  return <audio ref={setElement} autoPlay muted={muted} />;
}
